import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import {
  Info,
  ChevronDown,
  ChevronRight,
  Eye,
  AlertTriangle,
  Loader2,
  X,
} from 'lucide-react';
import { ScopeSelector } from './ScopeSelector';
import { ChatMarkdown } from '@plerionhq/design-system/unstable';
import type { CreateAgentSkillInput } from '../../../../api/agent-skills';
import { MetadataEditor } from './MetadataEditor';
import { WORKSPACE_OPTIONS } from '../utils/constants';

import { useTranslation, Trans } from 'react-i18next';

interface SkillFormProps {
  initial: CreateAgentSkillInput;
  onSave: (v: CreateAgentSkillInput) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  isEdit: boolean;
  isAdmin?: boolean;
  saveLabel?: string;
  error?: string | null;
  title?: string;
  subtitle?: string;
  tagNames?: string[];
}

type FieldErrors = Partial<
  Record<'name' | 'displayName' | 'description' | 'instructions', string>
>;

export function SkillForm({
  initial,
  onSave,
  onCancel,
  isSubmitting,
  isEdit,
  isAdmin = false,
  saveLabel,
  error,
  title,
  subtitle,
  tagNames,
}: SkillFormProps) {
  const { t } = useTranslation('pleri/skills');
  const [form, setForm] = useState(initial);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const nameRef = useRef<HTMLInputElement>(null);
  const displayNameRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const instructionsRef = useRef<HTMLTextAreaElement>(null);
  // Collapsed by default on edit, expanded on create
  const [showMetadata, setShowMetadata] = useState(!isEdit);
  const [showAdvanced, setShowAdvanced] = useState(
    !!(
      initial.license ||
      initial.compatibility ||
      initial.allowedTools?.length ||
      (initial.metadata && Object.keys(initial.metadata).length > 0)
    ),
  );

  const set = (key: keyof CreateAgentSkillInput, value: unknown) => {
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    setForm((f) => ({ ...f, [key]: value }));
  };

  const [tagInput, setTagInput] = useState('');
  const currentTags = form.tags ?? [];

  const addTag = (raw: string) => {
    const tag = raw.trim();
    if (!tag || currentTags.includes(tag)) return;
    set('tags', [...currentTags, tag]);
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    const next = currentTags.filter((t) => t !== tag);
    set('tags', next.length > 0 ? next : undefined);
  };

  const handleTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    } else if (e.key === 'Backspace' && !tagInput && currentTags.length > 0) {
      removeTag(currentTags[currentTags.length - 1]);
    }
  };

  const validate = (): boolean => {
    const errors: FieldErrors = {};
    if (!isEdit && !form.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!form.displayName.trim()) {
      errors.displayName = 'Display name is required';
    }
    if (!form.description.trim()) {
      errors.description = 'Description is required';
    }
    if (!form.instructions.trim()) {
      errors.instructions = 'Instructions are required';
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) {
      return true;
    }

    // Expand the metadata section so errors inside it are visible
    if (!showMetadata) {
      setShowMetadata(true);
    }

    // Focus and scroll to the first invalid field
    const firstErrorRef = errors.name
      ? nameRef
      : errors.displayName
        ? displayNameRef
        : errors.description
          ? descriptionRef
          : errors.instructions
            ? instructionsRef
            : null;

    // Small delay lets the metadata section finish expanding before scrolling
    setTimeout(() => {
      firstErrorRef?.current?.focus({ preventScroll: false });
      firstErrorRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 50);

    return false;
  };

  const lineCount = form.instructions.split('\n').length;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Title header — inside the form so it's part of the same flex column */}
      {title && (
        <div className="flex-shrink-0 border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {subtitle}
            </p>
          )}
        </div>
      )}
      {/* Scrollable form body */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {/* Scope selector — create only, scope is immutable after creation */}
        {!isEdit && (
          <div className="flex-shrink-0 border-b border-gray-200 px-6 py-5 dark:border-gray-700">
            <ScopeSelector
              value={form.scope ?? 'user'}
              onChange={(s) => set('scope', s)}
              isAdmin={isAdmin}
            />
          </div>
        )}

        {/* Collapsible metadata section */}
        <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowMetadata(!showMetadata)}
            type="button"
            className="flex w-full items-center gap-2 px-6 py-3 text-left text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            {showMetadata ? (
              <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200" />
            )}
            {t('skillSettings')}
            {!showMetadata && form.displayName && (
              <span className="ml-2 text-xs text-gray-400">
                {t('emDashName', { name: form.displayName })}
              </span>
            )}
          </button>

          <div
            className={`overflow-hidden transition-all duration-200 ${
              showMetadata ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="space-y-4 px-6 pb-4">
              {/* How skills work callout */}
              <div className="flex gap-2.5 rounded-md bg-orange-50 px-3.5 py-3 text-xs text-orange-800 dark:bg-orange-950 dark:text-orange-300">
                <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                <span>
                  <Trans
                    t={t}
                    i18nKey="pleriScansTheDescriptionOfEverySkill"
                    components={{ strong: <strong /> }}
                  />
                </span>
              </div>

              {!isEdit && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('common:name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    ref={nameRef}
                    className={`w-full rounded-md border px-3 py-2 font-mono text-sm dark:bg-gray-700 dark:text-gray-100 ${fieldErrors.name ? 'border-red-400 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                    placeholder={t('exampleSkillSlug')}
                    value={form.name}
                    onChange={(e) =>
                      set(
                        'name',
                        e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                      )
                    }
                  />
                  {fieldErrors.name ? (
                    <p className="mt-1 text-xs text-red-500">
                      {fieldErrors.name}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">
                      {t('onlyLowercaseLettersNumbersAndHyphensOther')}
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('displayName')} <span className="text-red-500">*</span>
                </label>
                <input
                  ref={displayNameRef}
                  className={`w-full rounded-md border px-3 py-2 text-sm dark:bg-gray-700 dark:text-gray-100 ${fieldErrors.displayName ? 'border-red-400 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                  placeholder={t('exampleSkillName')}
                  value={form.displayName}
                  onChange={(e) => set('displayName', e.target.value)}
                />
                {fieldErrors.displayName && (
                  <p className="mt-1 text-xs text-red-500">
                    {fieldErrors.displayName}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Trans
                    t={t}
                    i18nKey="tagsOptional"
                    components={{
                      span: (
                        <span className="text-xs font-normal text-gray-400" />
                      ),
                    }}
                  />
                </label>
                <div className="flex min-h-[38px] flex-wrap items-center gap-1.5 rounded-md border border-gray-300 px-2 py-1.5 focus-within:border-orange-400 focus-within:ring-1 focus-within:ring-orange-400 dark:border-gray-600 dark:bg-gray-700">
                  {currentTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-300"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-0.5 rounded-full p-0.5 hover:bg-orange-200 dark:hover:bg-orange-800"
                        aria-label={t('remove', { tag })}
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </span>
                  ))}
                  <input
                    list="skill-tag-datalist"
                    className="min-w-[120px] flex-1 bg-transparent text-sm outline-none placeholder-gray-400 dark:text-gray-100 dark:placeholder-gray-500"
                    placeholder={currentTags.length === 0 ? t('typeATagAndPressEnter') : t('addAnother')}
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    onBlur={() => { if (tagInput.trim()) addTag(tagInput); }}
                  />
                </div>
                {tagNames && tagNames.length > 0 && (
                  <datalist id="skill-tag-datalist">
                    {tagNames.filter((g) => !currentTags.includes(g)).map((g) => (
                      <option key={g} value={g} />
                    ))}
                  </datalist>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {t('pressEnterOrCommaToAddBackspace')}
                </p>
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('common:description')}{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <span
                    className={`text-xs tabular-nums ${
                      form.description.length > 900
                        ? 'text-red-500'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    {form.description.length}/1024
                  </span>
                </div>
                <textarea
                  ref={descriptionRef}
                  className={`w-full rounded-md border px-3 py-2 text-sm dark:bg-gray-700 dark:text-gray-100 ${fieldErrors.description ? 'border-red-400 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                  placeholder={t('skillDescriptionPlaceholder')}
                  rows={3}
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  maxLength={1024}
                />
                {fieldErrors.description && (
                  <p className="mt-1 text-xs text-red-500">
                    {fieldErrors.description}
                  </p>
                )}
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('workspaces')}
                  </label>
                  {(form.workspace ?? []).length > 0 && (
                    <button
                      type="button"
                      onClick={() => set('workspace', [])}
                      className="flex items-center gap-0.5 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="h-3 w-3" />
                      {t('clear')}
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {WORKSPACE_OPTIONS.map((ws) => {
                    const selected = (form.workspace ?? []).includes(ws);
                    return (
                      <button
                        key={ws}
                        type="button"
                        onClick={() => {
                          const current = form.workspace ?? [];
                          set(
                            'workspace',
                            selected
                              ? current.filter((w) => w !== ws)
                              : [...current, ws],
                          );
                        }}
                        className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                          selected
                            ? 'bg-orange-100 text-orange-700 ring-1 ring-orange-300 dark:bg-orange-900 dark:text-orange-300'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
                        }`}
                      >
                        {ws}
                      </button>
                    );
                  })}
                </div>
                {(form.workspace ?? []).length === 0 && (
                  <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                    {t('allWorkspacesDefault')}
                  </p>
                )}
                {(form.workspace ?? []).length > 0 && (
                  <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                    {t('activeIn', {
                      workspace: (form.workspace ?? []).join(', '),
                    })}
                  </p>
                )}
              </div>

              {/* Enabled checkbox */}
              <div className="border-t border-gray-100 pt-4 dark:border-gray-700">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => set('enabled', e.target.checked)}
                    className="h-4 w-4 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('enabled')}
                  </span>
                </label>
                <p className="mt-1 ml-6 text-xs text-gray-500">
                  {t('disabledSkillsAreHiddenFromPleri')}
                </p>
              </div>

              {/* Advanced fields */}
              <div>
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  type="button"
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  {showAdvanced ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                  {t('advancedFieldsOptional')}
                </button>

                {showAdvanced && (
                  <div className="mt-3 space-y-3 rounded-md border border-gray-200 p-3 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      <Trans
                        t={t}
                        i18nKey="theseFieldsFollowTheAgentSkillsSpec"
                        components={{
                          a: (
                            <a
                              href="https://agentskills.io/specification"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-orange-600 hover:text-orange-700"
                            />
                          ),
                        }}
                      />
                    </p>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                        {t('license')}
                      </label>
                      <input
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                        placeholder={t('exampleLicense')}
                        value={form.license ?? ''}
                        onChange={(e) =>
                          set('license', e.target.value || undefined)
                        }
                        maxLength={256}
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                        {t('compatibility')}
                      </label>
                      <input
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                        placeholder={t('eGRequiresGitDockerAndInternet')}
                        value={form.compatibility ?? ''}
                        onChange={(e) =>
                          set('compatibility', e.target.value || undefined)
                        }
                        maxLength={500}
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                        {t('allowedTools')}
                      </label>
                      <input
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                        placeholder={t('spaceDelimitedEGBashGitRead')}
                        value={(form.allowedTools ?? []).join(' ')}
                        onChange={(e) => {
                          const val = e.target.value.trim();
                          set(
                            'allowedTools',
                            val ? val.split(/\s+/) : undefined,
                          );
                        }}
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                        {t('metadata')}
                      </label>
                      <MetadataEditor
                        value={form.metadata ?? {}}
                        onChange={(v) =>
                          set(
                            'metadata',
                            Object.keys(v).length > 0 ? v : undefined,
                          )
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Instructions editor — side by side */}
        <div className="flex min-h-[400px] flex-1 flex-col">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-2 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('instructions')} <span className="text-red-500">*</span>
              </label>
              {fieldErrors.instructions && (
                <span className="text-xs text-red-500">
                  {fieldErrors.instructions}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400">
                {t('lineCount', { count: lineCount })}
              </span>
              <span
                className={`text-xs tabular-nums ${
                  form.instructions.length > 30000
                    ? 'text-red-500'
                    : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {form.instructions.length.toLocaleString()}/32,768
              </span>
            </div>
          </div>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:flex-row">
            {/* Left: textarea */}
            <div className="flex min-w-0 flex-1 flex-col border-b border-gray-200 lg:border-r lg:border-b-0 dark:border-gray-700">
              <textarea
                ref={instructionsRef}
                className={`min-h-[200px] flex-1 resize-none border-0 px-4 py-3 font-mono text-sm leading-relaxed focus:ring-0 focus:outline-none dark:text-gray-100 ${fieldErrors.instructions ? 'bg-red-50 dark:bg-red-950/20' : 'bg-transparent'}`}
                placeholder={t('skillInstructionsPlaceholder')}
                value={form.instructions}
                onChange={(e) => set('instructions', e.target.value)}
                maxLength={32768}
                spellCheck={false}
              />
            </div>
            {/* Right: live preview */}
            <div className="min-h-[200px] min-w-0 flex-1 overflow-hidden overflow-y-auto bg-gray-50 px-4 py-3 dark:bg-gray-900">
              <div className="mb-2 flex items-center gap-1 text-xs font-medium text-gray-400 uppercase">
                <Eye className="h-3 w-3" />
                {t('preview')}
              </div>
              <div className="prose prose-sm dark:prose-invert max-w-none break-words">
                <ChatMarkdown
                  content={
                    form.instructions || '*Start typing to see preview...*'
                  }
                />
              </div>
            </div>
          </div>
        </div>
        {/* End scrollable body */}
      </div>
      {/* Footer — pinned to bottom */}
      <div className="flex-shrink-0 border-t border-gray-200 px-6 py-3 dark:border-gray-700">
        {error && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            {t('common:cancel')}
          </button>
          <button
            onClick={() => {
              if (validate()) {
                onSave(form);
              }
            }}
            disabled={isSubmitting}
            className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t('saving')}
              </span>
            ) : saveLabel ? (
              saveLabel
            ) : isEdit ? (
              t('updateSkill')
            ) : (
              t('createSkill')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
